<template>
  <div
    v-if="getIsClient() && (isLoading || (!sections?.length && status === 'idle'))"
    class="flex flex-col"
  >
    <LcSkeleton class="mb-100" height="1.5rem" radius="medium" width="40%" />
    <LcSkeleton height="30rem" radius="medium" />
  </div>
  <div v-else class="info-faq flex flex-col gap-300">
    <template v-if="sections?.length">
      <section v-for="{ config, content } in sections" :key="config.name">
        <header class="flex items-center gap-100 pb-200">
          <LcIcon :name="config.icon" />

          <component :is="config.component" class="headline-h5-headline">
            {{ $t(`info.faq.section.${config.name}.title`) }}
          </component>
        </header>

        <LcExpandableList>
          <LcExpandable
            v-for="item in content"
            :id="item.sys.id"
            :key="item.sys.id"
            content-class="pt-200"
            :model-value="false"
            variant="enhanced"
          >
            <template #header>{{ item.caption }}</template>

            <ContentfulRenderer class="content" :data="item.content" />
          </LcExpandable>
        </LcExpandableList>
      </section>
    </template>
    <IllustratedSection v-else empty illustration="no-result">
      <template #headline>{{ $t('info.faq.noresults.headline') }}</template>
      <template #subtitle>{{ $t('info.faq.noresults.subline') }}</template>
    </IllustratedSection>
  </div>
</template>

<script lang="ts" setup>
import type { ContentBlock } from '#content/shared/@types/generated/cms/graphql-schema-types'
import type { InfoPageProps } from '~/custom-pages/info/types'

import { FAQ_COLLECTION_TYPES, getFaqCollectionType } from '#content/app/utils/faqCollection'

const toFaqBlock = (item: ContentBlock) => ({
  caption: item.caption || '',
  content: item.content,
  sys: { id: item.sys.id },
})

const COLLECTION_CONFIG = {
  [FAQ_COLLECTION_TYPES.PrizesAndWinnings]: {
    component: 'h2',
    icon: 'winning',
    name: 'faqprizesandwinnings',
  },
  [FAQ_COLLECTION_TYPES.TicketsAndDrawings]: {
    component: 'h2',
    icon: 'orders',
    name: 'faqticketsanddrawings',
  },
  [FAQ_COLLECTION_TYPES.TopQuestions]: {
    component: 'h1',
    icon: 'star',
    name: 'faqtopquestions',
  },
} as const

const { id, isVisible } = defineProps<InfoPageProps>()

const { data, execute, pending, status } = useContent().getFaq(id, {
  immediate: false,
})

const isLoading = useMinimumLoading(pending)

const sections = computed(() => {
  if (!data.value) return

  const isCollection = ([key, value]: [string, any]) => key.includes('Collection') && !!value

  return Object.entries(data.value)
    .filter(isCollection)
    .map(([_, value]) => ({
      config: COLLECTION_CONFIG[getFaqCollectionType(value.__typename)],
      content: value.items.map(toFaqBlock),
    }))
})

watchImmediate(
  () => isVisible,
  async (newValue) => {
    if (!newValue || data.value) return
    await execute()
  },
)

onServerPrefetch(execute)
</script>

<style lang="scss" scoped>
.info-faq {
  :deep(.content) {
    dl,
    ol,
    ul {
      margin-bottom: token('size/space/gap/200');
    }

    ol,
    ul {
      padding-left: token('size/space/gap/200');
      list-style: unset;

      ::marker {
        color: token('color/text/accent/accent/placeholder');
      }
    }
  }
}
</style>
